<template>
  <div class="pa-4">
    <div
      style="font-size: 30px;"
      class="font-weight-medium d-flex justify-space-between mb-3"
    >
      {{ eventItem.title }}
    </div>
    <div
      class="mt-2 mb-6 d-flex align-center"
      v-if="eventItem.domain != 'global'"
    >
      <div
        class="school-logo"
        :style="`border-color: ${getSchoolColour(eventItem.domain)}`"
      >
        <v-img
          :src="logo"
          width="22"
          height="22"
          contain
          class="flex-shrink-0 flex-grow-0"
        />
      </div>
      <div class="pl-2">
        {{ schoolName }}
      </div>
    </div>
    <div
      class="mt-2 mb-6 d-flex align-center"
      v-else
    >
      <div
        class="school-logo"
        :style="`border-color: ${getSchoolColour(eventItem.domain)}`"
      >
        <v-img
          src="/NSWGov_Logo-40.png"
          width="20"
          height="20"
          contain
          class="flex-shrink-0 flex-grow-0"
        />
      </div>
      <div class="pl-2">
        NSW Department of Education
      </div>
    </div>
    <div class="d-flex event-info pb-6 mb-6">
      <div class="mr-8 indigo--text darken-4 font-weight-medium">
        <div
          class="pb-1"
          v-if="eventItem.startDateTime"
        >
          {{ $t("calendar.event.startTime") }}:
        </div>
        <div
          class="pb-1"
          v-if="eventItem.endDateTime"
        >
          {{ $t("calendar.event.endTime") }}:
        </div>
        <div
          class="pb-1"
          v-if="eventItem.startText"
        >
          {{ $t("calendar.event.time") }}:
        </div>
        <div
          class="pb-1"
          v-if="eventItem.allDay"
        >
          {{ $t("calendar.event.time") }}:
        </div>
        <div class="pb-1">
          {{ $t("calendar.event.date") }}:
        </div>
        <div class="pb-1">
          {{ $t("calendar.event.venue") }}:
        </div>
      </div>
      <div class="flex-grow-1">
        <div
          class="pb-1"
          v-if="eventItem.startDateTime"
        >
          {{ startTime }}
        </div>
        <div
          class="pb-1"
          v-if="eventItem.endDateTime"
        >
          {{ endTime }}
        </div>
        <div
          class="pb-1"
          v-if="eventItem.startText"
        >
          {{ startTime }}
        </div>
        <div
          class="pb-1"
          v-if="eventItem.allDay"
        >
          {{ startTime }}
        </div>
        <div class="pb-1">
          {{ getDate(date) }}
        </div>
        <div
          class="d-flex pb-1"
          @click="openMapAddress(eventItem.location)"
        >
          <div>{{ eventItem.location }}</div>
          <div class="flex-grow-1 text-right">
            <v-btn
              icon
              small
            >
              <v-icon aria-label="open map">
                mdi-map-outline
              </v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>
    <div
      class="mb-3 event-description"
      style="font-size: 18px"
      v-html="eventItem.description"
    />
    <v-list>
      <v-list-item
        v-for="(attachment, index) in eventItem.attachments"
        :key="index"
        class="px-0 mb-3"
      >
        <AdsButton
          :secondary="true"
          class="d-flex event-attachment"
          @click="externalLink(attachment.url)"
        >
          <v-icon>
            mdi-paperclip
          </v-icon>
          <div class="flex-grow-1 text-left ml-4 attachment-details">
            <div>
              {{ attachment.name }}
            </div>
            <div class="attachment-info">
              {{ attachment.type }} {{ calculateSize(attachment.size) }}
            </div>
          </div>
          <v-icon>
            mdi-download-outline
          </v-icon>
        </AdsButton>
      </v-list-item>
    </v-list>
    <div
      class="mb-3"
      v-if="supportsReminders"
    >
      <h2 class="indigo--text darken-4 font-weight-medium mb-3">
        {{ $t("calendar.event.reminder") }}
      </h2>
      <div class="d-flex pb-1">
        <div class="">
          {{ reminderOptions[this.eventReminder].text }}
        </div>
        <v-dialog>
          <template v-slot:activator="{ on, attrs }">
            <div class="flex-grow-1 text-right">
              <v-btn
                icon
                small
                v-bind="attrs"
                @click="event => checkNotificationsEnabled(on, event)"
              >
                <v-icon aria-label="add reminder">
                  mdi-alarm-plus
                </v-icon>
              </v-btn>
            </div>
          </template>
          <template v-slot:default="dialog">
            <v-card>
              <v-card-text>
                <div class="pa-4">
                  <h2 class="indigo--text darken-4 font-weight-medium">
                    {{ $t("calendar.event.reminder") }}
                  </h2>
                  <v-list flat>
                    <v-list-item
                      v-for="(item, i) in reminderOptions"
                      :key="i"
                      @click="
                        setEventReminder(eventItem, date, i);
                        dialog.value = false;
                      "
                    >
                      <v-list-item-icon>
                        <v-icon
                          v-if="i === eventReminder"
                          class="indigo--text darken-4"
                        >
                          mdi-check
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title v-text="item.text" />
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>
              </v-card-text>
            </v-card>
          </template>
        </v-dialog>
      </div>
    </div>
    <div>
      <AdsButton
        :primary="true"
        :button-text="$t('calendar.removeEvent')"
        icon="calendar_today"
        style="width:auto"
        @click="removeCalendarEvent(eventItem, { schoolName, schoolCode })"
        v-if="eventBookmark && eventBookmark.uri"
      />
      <AdsButton
        :primary="true"
        :button-text="$t('calendar.addEvent')"
        icon="calendar_today"
        style="width:auto"
        @click="addCalendarEvent(eventItem, { schoolName, schoolCode })"
        v-else
      />
    </div>
  </div>
</template>
<script>
import { AppIcon, AdsButton, Dialog } from "@nswdoe/doe-ui-core";
import features from "../features.json";
import { DateTime } from "luxon";
import i18n from "@/i18n";

const REMINDER_OPTIONS = [
  {
    text: i18n.t("calendar.reminderNotification.none"),
    trigger: null
  },
  {
    text: i18n.t("calendar.reminderNotification.atTime"),
    trigger: 0
  },
  {
    text: i18n.t("calendar.reminderNotification.5Minutes"),
    trigger: 5
  },
  {
    text: i18n.t("calendar.reminderNotification.10Minutes"),
    trigger: 10
  },
  {
    text: i18n.t("calendar.reminderNotification.30Minutes"),
    trigger: 30
  },
  {
    text: i18n.t("calendar.reminderNotification.1Hour"),
    trigger: 60
  },
  {
    text: i18n.t("calendar.reminderNotification.2Hours"),
    trigger: 120
  }
];

const ALL_DAY_REMINDER_OPTIONS = [
  {
    text: i18n.t("calendar.reminderNotification.none"),
    trigger: null
  },
  {
    text: i18n.t("calendar.reminderNotification.onDay"),
    trigger: -540
  },
  {
    text: i18n.t("calendar.reminderNotification.dayBefore"),
    trigger: 900
  },
  {
    text: i18n.t("calendar.reminderNotification.2DaysBefore"),
    trigger: 2340
  },
  {
    text: i18n.t("calendar.reminderNotification.1WeekBefore"),
    trigger: 9540
  }
];

const EventInfo = {
  data: () => ({
    selectedReminder: 0
  }),
  components: {
    AppIcon,
    AdsButton,
    Dialog
  },
  name: "EventInfo",
  computed: {
    reminderOptions() {
      const allDay =
        this.eventItem.startDateTime == null &&
        this.eventItem.endDateTime == null;
      if (allDay) {
        return ALL_DAY_REMINDER_OPTIONS;
      } else {
        return REMINDER_OPTIONS;
      }
    },
    logo() {
      let schoolId = this.eventItem.domain;
      if (schoolId !== "global") {
        return this.schoolImages.filter(
          schoolInfo => schoolInfo.schoolId === schoolId
        )[0]?.logo;
      }
      return "my-nsw-gov";
    },
    schoolName() {
      let schoolId = this.eventItem.domain;
      if (schoolId !== "global") {
        return this.schoolImages.filter(
          schoolInfo => schoolInfo.schoolId === schoolId
        )[0]?.name;
      }
      return "global";
    },
    schoolCode() {
      let schoolId = this.eventItem.domain;
      if (schoolId !== "global") {
        return this.schoolImages.find(
          schoolInfo => schoolInfo.schoolId === schoolId
        )?.schoolCode;
      }
      return "global";
    },
    startTime() {
      if (this.eventItem.startText) {
        return this.eventItem.startText;
      } else if (this.eventItem.startDateTime) {
        return new Date(this.eventItem.startDateTime).toLocaleTimeString(
          this.getLocale(),
          {
            hour12: true,
            hour: "numeric",
            minute: "numeric"
          }
        );
      } else {
        return "All day";
      }
    },
    endTime() {
      if (this.eventItem.endText) {
        return this.eventItem.endText;
      } else if (this.eventItem.endDateTime) {
        return new Date(this.eventItem.endDateTime).toLocaleTimeString(
          this.getLocale(),
          {
            hour12: true,
            hour: "numeric",
            minute: "numeric"
          }
        );
      } else {
        return "All day";
      }
    },
    eventBookmark() {
      let ics = this.getCommonIcs(this.eventItem.ics);
      return this.$store.getters["eventBookmarks"].find(b => b.ics === ics);
    },
    eventReminder() {
      const eventId = `${this.eventItem.id}/${this.date}`;
      // Appending date to an otherwise event bookmark ID. This allows us to set reminder on individual recurring event.
      let reminderBookmark = this.$store.getters["eventBookmarks"].find(
        b => b.ics === eventId
      );
      if (reminderBookmark) {
        const reminderIndex = this.reminderOptions.findIndex(
          r => r.trigger === reminderBookmark.reminder
        );
        if (reminderIndex < 0) {
          return 0;
        } else {
          return reminderIndex;
        }
      } else {
        return this.selectedReminder;
      }
    },
    appBuild() {
      return this.getCookie("appbuild");
    },
    supportsReminders() {
      return this.appBuild >= features.calendarReminders;
    },
    locale() {
      return this.getCookie("locale");
    }
  },
  methods: {
    openMapAddress(address) {
      //eslint-disable-next-line
      NativeJS.debugLog(`opening address in maps (${address})`);
      //eslint-disable-next-line
      NativeJS.openMapAddress(address);
    },
    addCalendarEvent(event, school) {
      //eslint-disable-next-line
      NativeJS.debugLog(
        `adding event to calendar (${event.title} on the ${event.startDate})`
      );
      this.analytics("added_event", {
        school_name: school.schoolName.replace(" ", "-"),
        school_code: `S${school.schoolCode}`,
        label: event.title,
        item_category: "calendar"
      });
      if (parseInt(this.appBuild) >= features.calendarUpgrade) {
        const reminderTrigger = this.reminderOptions[this.selectedReminder]
          .trigger;
        const eventWithReminder = { ...event, reminder: reminderTrigger };

        //eslint-disable-next-line
        const eventUri = NativeJS.addCalendarEvent(
          JSON.stringify(reminderTrigger ? eventWithReminder : event)
        );
        let ics = this.getCommonIcs(event.ics);
        this.$store.dispatch("addEventUri", {
          event: eventWithReminder,
          uri: eventUri,
          ics: ics
        });
      } else {
        //eslint-disable-next-line
        NativeJS.addCalendarEvent(
          event.title ? event.title : "",
          event.description ? event.description : "",
          event.startDate ? event.startDate : "",
          event.endDate ? event.endDate : "",
          event.startText ? event.startText : "",
          event.endText ? event.endText : "",
          event.location ? event.location : ""
        );
      }
    },
    removeCalendarEvent(event, school) {
      //eslint-disable-next-line
      NativeJS.debugLog(`remove event from calendar (${event.title})`);
      //eslint-disable-next-line
      NativeJS.removeCalendarEvent(this.eventBookmark.uri);
      let ics = this.getCommonIcs(event.ics);
      this.$store.dispatch("removeEventUri", ics);

      this.analytics("remove_event", {
        school_name: school.schoolName.replace(" ", "-"),
        school_code: `S${school.schoolCode}`,
        label: event.title,
        item_category: "calendar"
      });
    },
    externalLink(link) {
      //eslint-disable-next-line
      NativeJS.debugLog(`opening external link (${link})`);
      //eslint-disable-next-line
      NativeJS.externalLink(link);
    },
    getDate(date) {
      return new Date(date).toLocaleDateString("en-AU", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    },
    calculateSize(bytes, decimals = 2) {
      if (bytes == 0 || bytes == null) return "External Link";

      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    },
    getSchoolColour(schoolId) {
      if (schoolId != "global") {
        return this.schoolImages.find(
          schoolInfo => schoolInfo.schoolId == schoolId
        )?.colour;
      }
      return "#264189";
    },
    getCommonIcs(ics) {
      let htmlIndex = ics.lastIndexOf("html");
      if (htmlIndex > -1) {
        return ics.slice(0, htmlIndex - 1);
      } else {
        return ics.slice(0, ics.length - 4);
      }
    },
    checkNotificationsEnabled(openDialog, event) {
      //eslint-disable-next-line
      if (NativeJS.checkNotificationsEnabled()) {
        openDialog.click(event);
      }
    },
    setEventReminder(event, date, reminderIndex) {
      this.selectedReminder = reminderIndex;
      const eventId = `${event.id}/${date}`;
      const trigger = this.reminderOptions[reminderIndex].trigger;

      if (trigger != null) {
        let scheduleDate;
        let eventDate = DateTime.fromFormat(date, "yyyy-MM-dd");
        if (!event.allDay) {
          // get the hours and minutes from the event
          const startDateTime = DateTime.fromISO(event.startDateTime);
          eventDate = eventDate.set({
            hour: startDateTime.get("hour"),
            minute: startDateTime.get("minute")
          });
        }
        scheduleDate = eventDate.minus({ minutes: trigger }).toMillis();
        //eslint-disable-next-line
        const setReminderSuccess = NativeJS.setEventReminder(
          JSON.stringify(event),
          scheduleDate
        );
        if (setReminderSuccess) {
          this.analytics("change_calendar_reminder", {
            item_category: "calendar",
            school_name: this.schoolName,
            school_code: `${this.schoolCode}`,
            label: this.reminderOptions[reminderIndex].text
          });

          this.$store.dispatch("setEventReminder", {
            ics: `${eventId}`,
            reminder: trigger
          });
        }
      } else {
        //eslint-disable-next-line
        NativeJS.removeEventReminder(eventId);
        this.analytics("change_calendar_reminder", {
          item_category: "calendar",
          school_name: this.schoolName,
          school_code: `${this.schoolCode}`,
          label: this.reminderOptions[0].text
        });

        this.$store.dispatch("setEventReminder", {
          ics: `${eventId}`,
          reminder: trigger
        });
      }
    }
  },
  props: {
    eventItem: {
      type: Object,
      default: function() {
        return {};
      }
    },
    schoolImages: {
      type: Array,
      default: function() {
        return [];
      }
    },
    date: {
      type: String,
      default: ""
    }
  },
  watch: {
    eventItem: function() {
      this.selectedReminder = 0;
    }
  }
};

export default EventInfo;
</script>
<style lang="scss" scoped>
.event-info {
  border-bottom: 1px solid #c8dcf0;
}
.event-description {
  white-space: pre-line;
}
.event-attachment {
  width: 100%;
  border-color: #dde9f6;
  height: 56px !important;
  border-width: 1px !important;
  .attachment-details {
    white-space: normal;
    .attachment-info {
      font-size: 13px;
      font-weight: 500;
    }
  }
}
.event-attachment::v-deep .v-btn__content {
  justify-content: flex-start;
  width: 100%;
}
.school-logo {
  padding: 7px;
  border: 2px solid;
  border-radius: 100px;
}
h2 {
  font-size: 1.125rem;
}
</style>
